<!-- src/components/comparisons/RunRateContent.vue -->

<template>
  <div class="run-rate-content">
    <div v-if="loading" class="center-align run-rate-state">
      <div class="preloader-wrapper small active">
        <div class="spinner-layer spinner-blue-only">
          <div class="circle-clipper left">
            <div class="circle"></div>
          </div>

          <div class="gap-patch">
            <div class="circle"></div>
          </div>

          <div class="circle-clipper right">
            <div class="circle"></div>
          </div>
        </div>
      </div>

      <p class="grey-text">Calculating run rate...</p>
    </div>

    <div v-else-if="errorMessage" class="card-panel red lighten-5 red-text text-darken-2">
      {{ errorMessage }}
    </div>

    <div v-else class="run-rate-table-wrapper">
      <table class="striped highlight responsive-table run-rate-table">
        <thead>
          <tr>
            <th>Monday</th>
            <th>Tuesday</th>
            <th>Wednesday</th>
            <th>Thursday</th>
            <th>Friday</th>
            <th>Saturday</th>
            <th>Sunday</th>
            <th>Total</th>
          </tr>
        </thead>

        <tbody>
          <tr>
            <td>
              {{ formatCurrency(runRate.monday) }}
            </td>

            <td>
              {{ formatCurrency(runRate.tuesday) }}
            </td>

            <td>
              {{ formatCurrency(runRate.wednesday) }}
            </td>

            <td>
              {{ formatCurrency(runRate.thursday) }}
            </td>

            <td>
              {{ formatCurrency(runRate.friday) }}
            </td>

            <td>
              {{ formatCurrency(runRate.saturday) }}
            </td>

            <td>
              {{ formatCurrency(runRate.sunday) }}
            </td>

            <td>
              <strong>
                {{ formatCurrency(runRateTotal) }}
              </strong>
            </td>
          </tr>
        </tbody>
      </table>

      <div class="run-rate-information grey-text text-darken-1">
        Run rate uses the latest 6 available equivalent trading days. Where the current
        week's actual revenue has been entered, it is included and replaces the oldest
        week in the 6-week average.
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from "vue";

import { collection, doc, getDoc, getDocs } from "firebase/firestore";

import { db } from "@/firebase";

import { useVenueContext } from "@/scripts/useScripts/useVenueContext";

const { venueId, weekEnding } = useVenueContext();

const loading = ref(false);

const errorMessage = ref("");

function emptyRunRate() {
  return {
    monday: 0,
    tuesday: 0,
    wednesday: 0,
    thursday: 0,
    friday: 0,
    saturday: 0,
    sunday: 0,
  };
}

const runRate = ref(emptyRunRate());

const dayDefinitions = [
  {
    key: "monday",
    offsetFromSunday: -6,
  },
  {
    key: "tuesday",
    offsetFromSunday: -5,
  },
  {
    key: "wednesday",
    offsetFromSunday: -4,
  },
  {
    key: "thursday",
    offsetFromSunday: -3,
  },
  {
    key: "friday",
    offsetFromSunday: -2,
  },
  {
    key: "saturday",
    offsetFromSunday: -1,
  },
  {
    key: "sunday",
    offsetFromSunday: 0,
  },
];

function cleanNumber(value) {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : 0;
  }

  if (typeof value === "string") {
    const cleaned = value.replace(/£/g, "").replace(/,/g, "").trim();

    if (cleaned === "") {
      return 0;
    }

    const number = Number(cleaned);

    return Number.isFinite(number) ? number : 0;
  }

  return 0;
}

function formatCurrency(value) {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(cleanNumber(value));
}

function parseISODate(value) {
  if (!value) {
    return null;
  }

  const rawValue = String(value).trim();

  const match = rawValue.match(/^(\d{4})-(\d{2})-(\d{2})$/);

  if (!match) {
    const fallbackDate = new Date(rawValue);

    return Number.isNaN(fallbackDate.getTime()) ? null : fallbackDate;
  }

  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 12, 0, 0, 0);
}

function formatISODate(date) {
  const year = date.getFullYear();

  const month = String(date.getMonth() + 1).padStart(2, "0");

  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

function addDays(date, numberOfDays) {
  const result = new Date(date);

  result.setDate(result.getDate() + numberOfDays);

  return result;
}

function extractTillDocTotal(data) {
  if (!data || typeof data !== "object") {
    return 0;
  }

  const directValue =
    data.counted_total ??
    data.countedTotal ??
    data.total_counted ??
    data.actual_total ??
    data.actualTotal ??
    data.total ??
    data.grand_total ??
    data.grandTotal ??
    data.value ??
    data.amount ??
    data?.totals?.counted_total ??
    data?.totals?.total ??
    null;

  if (directValue != null) {
    return cleanNumber(directValue);
  }

  const ignoredFields = new Set([
    "name",
    "label",
    "type",
    "id",
    "uid",
    "email",
    "updated_at",
    "updatedAt",
    "created_at",
    "createdAt",
    "venue_id",
    "venueId",
    "trading_date",
    "tradingDate",
    "week_ending_date",
    "weekEndingDate",
  ]);

  let total = 0;

  for (const [key, value] of Object.entries(data)) {
    if (ignoredFields.has(key)) {
      continue;
    }

    if (typeof value === "number" && Number.isFinite(value)) {
      total += value;
    }
  }

  return total;
}

async function fetchActualForDay(activeVenueId, tradingDateIso) {
  const dayReference = doc(db, "venues", activeVenueId, "tills_days", tradingDateIso);

  const daySnapshot = await getDoc(dayReference);

  if (daySnapshot.exists()) {
    const dayData = daySnapshot.data() || {};

    const rolledTotal =
      dayData.counted_total ??
      dayData.countedTotal ??
      dayData.total_counted ??
      dayData.actual_total ??
      dayData.actualTotal ??
      dayData.total ??
      dayData?.totals?.counted_total ??
      null;

    if (rolledTotal != null && rolledTotal !== "") {
      return {
        exists: true,

        value: cleanNumber(rolledTotal),
      };
    }
  }

  const tillsSnapshot = await getDocs(
    collection(db, "venues", activeVenueId, "tills_days", tradingDateIso, "tills")
  );

  if (tillsSnapshot.empty) {
    return {
      exists: false,

      value: 0,
    };
  }

  let total = 0;

  tillsSnapshot.forEach((tillSnapshot) => {
    total += extractTillDocTotal(tillSnapshot.data() || {});
  });

  return {
    exists: true,

    value: total,
  };
}

function average(values) {
  if (!Array.isArray(values) || values.length === 0) {
    return 0;
  }

  const total = values.reduce((sum, value) => sum + cleanNumber(value), 0);

  return total / values.length;
}

const runRateTotal = computed(() => {
  return (
    cleanNumber(runRate.value.monday) +
    cleanNumber(runRate.value.tuesday) +
    cleanNumber(runRate.value.wednesday) +
    cleanNumber(runRate.value.thursday) +
    cleanNumber(runRate.value.friday) +
    cleanNumber(runRate.value.saturday) +
    cleanNumber(runRate.value.sunday)
  );
});

let loadSequence = 0;

async function loadRunRate() {
  const sequence = ++loadSequence;

  errorMessage.value = "";

  runRate.value = emptyRunRate();

  const activeVenueId = venueId.value;

  const selectedWeekEnding = weekEnding.value;

  if (!activeVenueId || !selectedWeekEnding) {
    return;
  }

  const selectedSunday = parseISODate(selectedWeekEnding);

  if (!selectedSunday) {
    errorMessage.value = "Unable to calculate the run rate for this week.";

    return;
  }

  loading.value = true;

  try {
    const requests = [];

    for (const definition of dayDefinitions) {
      for (let weeksBack = 0; weeksBack <= 6; weeksBack += 1) {
        const tradingDate = addDays(
          selectedSunday,
          definition.offsetFromSunday - weeksBack * 7
        );

        requests.push({
          dayKey: definition.key,

          weeksBack,

          tradingDateIso: formatISODate(tradingDate),
        });
      }
    }

    const results = await Promise.all(
      requests.map(async (request) => ({
        ...request,

        actual: await fetchActualForDay(activeVenueId, request.tradingDateIso),
      }))
    );

    if (sequence !== loadSequence) {
      return;
    }

    const calculated = emptyRunRate();

    for (const definition of dayDefinitions) {
      const dayResults = results
        .filter((item) => item.dayKey === definition.key)
        .sort((a, b) => a.weeksBack - b.weeksBack);

      const current = dayResults.find((item) => item.weeksBack === 0);

      const weeksToUse = current?.actual?.exists
        ? dayResults.filter((item) => item.weeksBack <= 5)
        : dayResults.filter((item) => item.weeksBack >= 1 && item.weeksBack <= 6);

      const values = weeksToUse
        .filter((item) => item.actual.exists)
        .map((item) => item.actual.value);

      calculated[definition.key] = average(values);
    }

    runRate.value = calculated;
  } catch (error) {
    console.error("Failed to calculate run rate:", error);

    if (sequence === loadSequence) {
      errorMessage.value = "Unable to calculate the run rate for this week.";
    }
  } finally {
    if (sequence === loadSequence) {
      loading.value = false;
    }
  }
}

watch(
  [venueId, weekEnding],

  () => {
    loadRunRate();
  },

  {
    immediate: true,
  }
);
</script>

<style scoped>
.run-rate-content {
  width: 100%;
}

.run-rate-state {
  padding: 30px 10px;
}

.run-rate-table-wrapper {
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.run-rate-table {
  min-width: 900px;
}

.run-rate-table th,
.run-rate-table td {
  text-align: right;
  white-space: nowrap;
}

.run-rate-table th:first-child,
.run-rate-table td:first-child {
  padding-left: 16px;
}

.run-rate-table th:last-child,
.run-rate-table td:last-child {
  padding-right: 16px;
  font-weight: 600;
}

.run-rate-information {
  margin-top: 14px;
  font-size: 0.9rem;
}

@media (max-width: 600px) {
  .run-rate-table {
    min-width: 850px;
  }

  .run-rate-information {
    padding-left: 4px;
    padding-right: 4px;
  }
}
</style>
